import { FieldProps, getIn } from 'formik';
import { ChangeEvent, FocusEvent, useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { TextField, TextFieldProps } from '../TextField/TextField';
import { getDataIdGenerator } from '../../shared/utils/dataId';
import { useDebounce } from '../../shared/hooks/useDebounce';
import {
  preventEnteringParticularChar,
  preventEnteringSpecialChar
} from '../../shared/utils/input';
import { FormHelperText } from '../../shared/components/FormHelperText/FormHelperText';
import styles from './FormikTextField.module.scss';

type FormikTextFieldAdditionalProps = {
  debounceUpdates?: boolean;
  hideHelperMessageIfEmpty?: boolean;
  disallowChar?: string;
  queryDebounceTime?: number;
  onChange?: (value: string | number) => void;
  errorMessageClassName?: string;
  inlineError?: boolean;
  convertFrom?: (value: string | number) => string | number;
  convertTo?: (value: string | number) => string | number;
};

export type FormikTextFieldProps = Omit<TextFieldProps, 'onChange'> &
  FormikTextFieldAdditionalProps;

export function FormikTextField({
  field,
  form: { touched, errors, setFieldValue: setFormikFieldValue },
  label,
  placeholder,
  id,
  helperText = null,
  hideHelperMessageIfEmpty = true,
  onBlur,
  debounceUpdates = false,
  onChange,
  onFocus,
  disallowChar,
  dataId,
  queryDebounceTime = 500,
  type,
  errorMessageClassName,
  inlineError,
  convertFrom = (v: string | number) => v,
  convertTo = (v: string | number) => v,
  ...props
}: FormikTextFieldProps & FieldProps) {
  const [inputValue, setInputValue] = useState<string | number>(convertFrom(field.value) || '');
  const debouncedInputValue = useDebounce(inputValue, queryDebounceTime);

  const showError =
    (!!touched[field.name] && !!errors[field.name]) ||
    (!!getIn(errors, field.name) && !!getIn(touched, field.name));

  const setFieldValue = (value: string | number) => {
    const convertedValue = convertTo(value);

    setFormikFieldValue(field.name, convertedValue);

    if (onChange) {
      onChange(convertedValue);
    }
  };

  useEffect(() => {
    if (debouncedInputValue !== field.value) {
      setInputValue(convertFrom(field.value));
    }
  }, [field.value, convertFrom]);

  useEffect(() => {
    if (debounceUpdates && debouncedInputValue !== field.value) {
      setFieldValue(debouncedInputValue);
    }
  }, [debounceUpdates, debouncedInputValue, field.name]);

  let conditionalProps = {};
  if (type === 'number') {
    conditionalProps = { onKeyDown: preventEnteringSpecialChar };
  }

  if (disallowChar) {
    conditionalProps = {
      onKeyDown: (event: KeyboardEvent) => preventEnteringParticularChar(event, disallowChar)
    };
  }

  const onBlurEvent = (e: FocusEvent<HTMLInputElement>) => {
    field.onBlur(e);

    if (onBlur) {
      onBlur(e);
    }
  };

  const onFocusEvent = (e: FocusEvent<HTMLInputElement>) => {
    if (onFocus) {
      onFocus(e);
    }
  };

  const onChangeEvent = async (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = e.target;

    setInputValue(value);

    if (!debounceUpdates) {
      const _value = type === 'number' ? Number(value) : value;

      setFieldValue(_value);
    }
  };

  const dataIdGenerator = getDataIdGenerator(
    `${dataId ? `${dataId}-` : ''}${field.name}`,
    'textField'
  );

  return (
    <TextField
      id={id || field.name}
      label={label}
      placeholder={placeholder || label ? String(label) : ''}
      helperText={FormHelperText({
        helperText,
        showError,
        hideHelperMessageIfEmpty,
        fieldName: field.name,
        errorMessage: getIn(errors, field.name),
        errorClassName: clsx(inlineError && styles.inlineError, errorMessageClassName)
      })}
      {...field}
      {...props}
      dataId={dataIdGenerator('')}
      value={inputValue || ''}
      error={showError}
      autoComplete='off'
      type={type}
      onBlur={onBlurEvent}
      onChange={onChangeEvent}
      onFocus={onFocusEvent}
      {...conditionalProps}
    />
  );
}
